import { ForbiddenException, SchemaErrorException, SelectableProfile } from '@nemuru-eng/js-api-client';
import { useAuth } from '@nemuru-eng/providers';
import { useState } from 'react';

import { saveProfileSwitchHandoff } from '@/components/shared/components/ProfileSwitchOverlay/services/profileSwitchHandoff';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';
import { paths } from '@/Routes';

const MIN_SWITCH_DURATION_MS = 800;

const wait = (durationMs: number) => new Promise<void>((resolve) => setTimeout(resolve, durationMs));

/** Errors that are raised before any token could have moved: the refusal of the API and the client-side validation. */
const isSessionUnchangedError = (error: unknown): boolean =>
  error instanceof ForbiddenException || error instanceof SchemaErrorException;

/**
 * Switches the session to another profile of the same person and sends the app back to home with a full
 * page load: UserContext caches the user once at mount, so nothing short of a reload is consistent. A
 * refusal (403: not theirs, inactive, blocked, or Kong) or an invalid request shows a snackbar and leaves
 * everything as it was; any other failure redirects too, because the tokens may already have moved
 * (providers' switchProfile contract). The handoff lets the reloaded app report a switch that did not end
 * on the requested profile.
 */
export const useProfileSwitch = () => {
  const { switchProfile, profiles } = useAuth();
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();
  const [switchingProfile, setSwitchingProfile] = useState<SelectableProfile | undefined>(undefined);

  const redirectToHome = (profile: SelectableProfile) => {
    saveProfileSwitchHandoff({ profile });
    window.location.replace(paths.App);
  };

  const switchTo = async (profileId: string): Promise<void> => {
    const profile = profiles?.find(({ id }) => id === profileId);
    if (!profile) return;

    setSwitchingProfile(profile);
    const minimumDuration = wait(MIN_SWITCH_DURATION_MS);

    try {
      await switchProfile(profileId);
    } catch (error) {
      if (isSessionUnchangedError(error)) {
        await minimumDuration;
        setSwitchingProfile(undefined);
        setSnackbarNotification({ msg: 'PROFILE_SWITCH_ERROR', level: 'error', error });
        return;
      }
    }

    await minimumDuration;
    redirectToHome(profile);
  };

  return { switchingProfile, switchingTo: switchingProfile?.id, switchTo };
};
